import { Button, Theme } from "@astryxdesign/core";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { LoopNotes } from "./LoopNotes";
import { hireosTheme } from "@/theme/hireos";

// The film and its poster carry the date they were cut, because Hosting
// serves an .mp4 with a week's cache and the path never changed: four
// recuts in one afternoon all reached a browser that had the first one and
// was not going to ask again. Bump it when the film or the loop is recut.
const CUT = "2026-10-07";
export const FILM = `/demo.mp4?v=${CUT}`;
// A phone gets the film cropped to 608x1080 around what matters in each shot,
// by build-cut.sh, on the cuts' own frames. Moving the crop in the browser
// landed it a frame or two after each cut, and read as the picture sliding.
export const TALL_FILM = `/demo-portrait.mp4?v=${CUT}`;
// The loop is the film's good half, shots 5 to 7, muted and seamless. It is
// built by build-loop.sh beside the film's own build-cut.sh, with nothing
// drawn on it: LoopNotes draws her card and the crew's mail over it, so they
// can be kept under the nav on any screen.
const LOOP = `/hero-loop.mp4?v=${CUT}`;
const LOOP_POSTER = `/hero-loop.webp?v=${CUT}`;
// A phone gets the loop made tall: the same cut from the shots without the
// drawn notes, widened to 9:16 by fal's LTX 2.3 Reframe, then cut again and
// cropped around each shot by build-loop-tall.sh beside build-loop.sh.
export const PHONE = "(max-width: 660px)";
const TALL_LOOP = `/hero-loop-portrait.mp4?v=${CUT}`;
const TALL_POSTER = `/hero-loop-portrait.webp?v=${CUT}`;

// The film's cuts, by frame at 24fps, and the time halfway into the frame
// before each, so that a change keyed to a cut lands on the cut's own frame
// and not one either side of it: the first frame of a shot in the last one's
// crop is the picture jumping.
const CUT_AT = (frame: number) => (frame - 0.5) / 24;
const [C2, C3, C4, C5, C6, C7] = [95, 214, 346, 480, 686, 825].map(CUT_AT);
// The dissolve to the end card starts here
const C8 = 40.14;

// Where and when each shot is, drawn over the film in the bottom left of
// whatever the player shows. The film on the site carries none of its own
// (build-cut.sh), because a phone's crop cut the burned-in ones off. They
// change on the cuts; none over the end card.
type Slate = [from: number, to: number, place: string, title?: string];
const SLATES: Slate[] = [
  [0, C2, "Lake Hayes, Otago · Sat 14 March, 2:41 pm", "James's farewell party"],
  [C2, C3, "Lake Hayes, Otago · 2:42 pm"],
  [C3, C4, "Wakatipu Sound & Light, Frankton · 2:44 pm"],
  [C4, C5, "Wakatipu Sound & Light, Frankton · 2:45 pm"],
  [C5, C6, "Lake Hayes, Otago · 2:46 pm"],
  [C6, C7, "Frankton Road, Queenstown · 4:12 pm"],
  [C7, C8, "Lake Hayes, Otago · 7:58 pm"],
];

/**
 * The film, in its own card part way down the page, over a loop of its good
 * half. It was the hero; the hero is the product now, and this is the story
 * of one call through it.
 *
 * A press anywhere on it that is not a button plays the film itself over the
 * whole card, where there is a pointer; on a touch screen a tap is as often
 * the start of a scroll, so there only the button does. The close button,
 * Esc, a press on the film or its end put the loop back. The film is not
 * fetched until it is asked for.
 */
export function Reel() {
  const card = useRef<HTMLElement>(null);
  const film = useRef<HTMLVideoElement>(null);
  const loop = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [still] = useState(() => matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [tall] = useState(() => matchMedia(PHONE).matches);

  const play = useCallback(() => {
    const v = film.current;
    if (!v) return;
    v.currentTime = 0;
    // The card fills the screen, so it is brought to the top of it, inside
    // the 20px the card is inset by.
    const top = (card.current?.parentElement?.getBoundingClientRect().top ?? 0) + scrollY;
    window.scrollTo({ top, behavior: "smooth" });
    setPlaying(true);
    void v.play();
  }, []);

  const stop = useCallback(() => {
    film.current?.pause();
    setPlaying(false);
  }, []);

  // Esc stops the film, and so does scrolling away from it: the card is
  // scrolled into place when it starts, so any scroll of more than a little
  // after that is the reader leaving, and a film playing on behind them is
  // noise. The scroll it does itself on the way is over before this is armed.
  useEffect(() => {
    if (!playing) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && stop();
    let from = 0;
    const onScroll = () => Math.abs(scrollY - from) > 80 && stop();
    addEventListener("keydown", onKey);
    const arm = setTimeout(() => {
      from = scrollY;
      addEventListener("scroll", onScroll, { passive: true });
    }, 900);
    return () => {
      removeEventListener("keydown", onKey);
      clearTimeout(arm);
      removeEventListener("scroll", onScroll);
    };
  }, [playing, stop]);

  return (
    <section
      className={`hero${playing ? " playing" : ""}`}
      id="film"
      ref={card}
      onClick={(e) => {
        if (playing || (e.target as Element).closest("a, button")) return;
        if (matchMedia("(hover: hover)").matches) play();
      }}
    >
      <video
        className="loop"
        ref={loop}
        src={tall ? TALL_LOOP : LOOP}
        poster={tall ? TALL_POSTER : LOOP_POSTER}
        autoPlay={!still}
        muted
        loop
        playsInline
        preload={still ? "none" : "auto"}
        aria-hidden="true"
      />
      <div className="tint" />
      <LoopNotes loop={loop} tall={tall} />

      <video
        className="film"
        ref={film}
        src={tall ? TALL_FILM : FILM}
        playsInline
        preload="none"
        aria-label="HireOS film"
        onClick={stop}
        onEnded={stop}
      />
      {/* A phone's copy of the film is clean (build-cut-tall.sh), as the tall
          loop is, so its notes are drawn over it the same way */}
      {tall && playing && <LoopNotes loop={film} tall film />}

      {/* The sign that the hero plays the film: a triangle in the middle of
          it, shown while the pointer is over the hero, and always on a touch
          screen. */}
      <button className="watch" type="button" onClick={play} inert={playing} aria-label="Watch the full film">
        {/* Clear, its edge drawn as a white hairline, as the buttons are. */}
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M7 6.9V17.1Q7 19.5 9.058 18.265L17.442 13.235Q19.5 12 17.442 10.765L9.058 5.735Q7 4.5 7 6.9Z"
            fill="none"
            stroke="rgba(255, 255, 255, 0.85)"
            strokeWidth="0.364"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="l" inert={playing}>
        <h2>One call, start to finish.</h2>
        <div>
          <p>
            Hannah rings to change her booking while the crew are out on a job. Watch where her call
            ends up.
          </p>
          {/* On the film, so in the theme's dark mode: white text. On a phone
              the triangle would sit on her face, so this is the way in. */}
          <div className="btns">
            <Theme theme={hireosTheme} mode="dark">
              <Button
                label="Watch the film"
                variant="ghost"
                icon={
                  <svg className="tri" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M7 6.9V17.1Q7 19.5 9.058 18.265L17.442 13.235Q19.5 12 17.442 10.765L9.058 5.735Q7 4.5 7 6.9Z" />
                  </svg>
                }
                onClick={play}
              />
            </Theme>
          </div>
        </div>
      </div>

      <FilmUI film={film} playing={playing} onClose={stop} />
    </section>
  );
}

/**
 * The film's own player, over whatever it plays in: the close button (if it
 * has one), where and when the shot is, and a hairline of how far in it is. Nothing else, so
 * there is no browser's bar to fullscreen, mute or menu.
 */
export function FilmUI({
  film,
  playing,
  onClose,
}: {
  film: RefObject<HTMLVideoElement | null>;
  playing: boolean;
  onClose?: () => void;
}) {
  const bar = useRef<HTMLElement>(null);
  const slate = useRef<HTMLDivElement>(null);

  // While the film plays, its slate and how far in it is, checked every frame
  // and written straight into the page, the slate only when it changes.
  useEffect(() => {
    const v = film.current;
    if (!playing || !v) return;
    let shown: Slate | undefined;
    let raf = 0;
    const tick = () => {
      const t = v.currentTime;
      if (bar.current) bar.current.style.transform = `scaleX(${t / (v.duration || 1)})`;
      const now = SLATES.find(([a, b]) => t >= a && t < b);
      const el = slate.current;
      if (now !== shown && el) {
        shown = now;
        el.children[0]!.textContent = now?.[3] ?? "";
        el.children[1]!.textContent = now?.[2] ?? "";
        el.hidden = !now;
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [film, playing]);

  return (
    <div className="filmui" inert={!playing}>
      {onClose && (
        <button className="close" type="button" aria-label="Close the film" onClick={onClose}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </button>
      )}
      <div className="slate" ref={slate} hidden>
        <b />
        <span />
      </div>
      <div className="progress">
        <i ref={bar} />
      </div>
    </div>
  );
}
